<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { filter, get, orderBy, groupBy } from "lodash";
import { translation, translateToEn } from '@/plugins/i18n';
import Tile from '@/components/tile.vue';
import {
  BASE_PLAN_SUB_TYPES,
  BILLING_CYCLE,
  planTypes, ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE,
  ZOOM_MEETING_PRO_MIN_LICENSE,
} from '@/common/constants';
import base from '@/views/config/addon-cards/templates/base.vue';
import logo from '@/assets/plans/zoom_one_pro_addon.svg';
import { DEFAULT_MEETING_PLAN_CAPACITY, DEFAULT_MEETING_BIZ_PLAN_CAPACITY, isBizPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { AddOnGroupTrackingUILocation, getTrackingFlowType } from '@/aspects/trackings';

const def2biz = isBizPlusContext;
const addOnMeetingMap = () => {
  return {
    [BASE_PLAN_SUB_TYPES.PRO]: {
      title: translation('billing.planform.title.zoom_one_pro'),
      trackingPlanName: translateToEn('billing.planform.title.zoom_one_pro'),
      form: DEFAULT_MEETING_PLAN_CAPACITY,
      description: translation('billing.zoom_one_pro.desc'),
      category: BASE_PLAN_SUB_TYPES.PRO,
    },
    [BASE_PLAN_SUB_TYPES.BIZ]: {
      title: translation('billing.planform.title.zoom_one_biz'),
      trackingPlanName: translateToEn('billing.planform.title.zoom_one_biz'),
      form: DEFAULT_MEETING_BIZ_PLAN_CAPACITY,
      description: translation('billing.zoom_one_pro.desc'),
      category: BASE_PLAN_SUB_TYPES.BIZ,
      selectedNumber: ZOOM_MEETING_PRO_MIN_LICENSE,
    },
    [BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP]: {
      title: translation('billing.planform.title.zo_edu_sch_cmp'),
      trackingPlanName: translateToEn('billing.planform.title.zo_edu_sch_cmp'),
      form: {
        category: BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP,
        billingCycle: 'annual',
        selectedNumber: ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE,
      },
      description: translation('billing.zone_edu_plus.desc'),
      category: BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP,
    },
  };
};

export default {
  name: 'ZoomMeetingAddOnCard',
  mixins: [base],
  components: {
    Tile
  },
  created() {
    const { amount } = this.lowestPlan;
    this.subtitle = this.$t('billing.common.form_xx_month', [this.$money(amount)]);
    this.productPlans = this.currentProductPlans;
    //
    this.$root.$on('trigger-add-zoom-one', () => this.$emit('add'));
  },
  data() {
    return {
      logo,
      subtitle: '',
      planType: planTypes.ZOOM_MEETING,
    }
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['pastPlans']),
    recommendType(){
      const { categoryCode } = this.pastPlans.find(item => item.semanticType === planTypes[planTypes.ZOOM_MEETING]) || {};
      if (categoryCode && addOnMeetingMap()[categoryCode.toLowerCase()]) {
        return categoryCode.toLowerCase();
      }
      return def2biz ? BASE_PLAN_SUB_TYPES.BIZ : BASE_PLAN_SUB_TYPES.PRO;
    },
    currentPlan() {
      return addOnMeetingMap()[this.recommendType];
    },
    currentProductPlans() {
      return orderBy(filter(this.planMapFlat, (p) => p.type === planTypes.ZOOM_MEETING && p.category === this.recommendType && !p.isOneMonth), ['participants', 'amount'], 'asc');
    },
    lowestPlan() {
      return get(this.currentProductPlans, [0], {});
    },
    zonePlans() {
      return this.planMapFlat.filter((item) => item.type === planTypes.ZOOM_MEETING);
    },
    zonePlanGroupByCategory() {
      return groupBy(this.zonePlans, 'category');
    },
    // current selected meeting plan
    formPlan() {
      return this.zonePlanGroupByCategory[this.form.category].find(
        (item) => item.billing_cycle === this.form.billingCycle,
      );
    },
    priceInfo() {
      const result = {
        monthlyUnit: 'billing.common.per_billing_cycle_user.monthly',
        annualUnit: 'billing.common.per_billing_cycle_user.annual',
        formattedMonthlyAmount: '',
        formattedAnnualAmount: ''
      }
      const plans = this.zonePlanGroupByCategory[this.form.category]
      plans?.forEach((plan) => {
        if (plan.billing_cycle === BILLING_CYCLE.MONTHLY) {
          result.formattedMonthlyAmount = this.$money(plan.amount)
        } else if (plan.billing_cycle === BILLING_CYCLE.ANNUAL) {
          result.formattedAnnualAmount = this.$money(plan.amount)
        }
      })
      return result
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    addOn(evt) {
      // addPlanFromEnterKey For A11Y requirement
      // target is BUTTON comes from keyboard enter, will auto focaus on the first option in config card
      // target is SPAN comes from click
      const addPlanFromEnterKey = get(evt?.target, 'tagName', '') == 'BUTTON';
      if (addPlanFromEnterKey) {
        document.addPlanFromEnterKey = true;
      }
      const selectedNumber = this.pastPlans?.length > 0 && this.currentPlan?.selectedNumber ? this.currentPlan.selectedNumber : this.currentPlan.form?.selectedNumber || 1;
      this.updateSelectPlan({
        ...this.formPlan,
        cartInfo: {
          selected_number: selectedNumber,
        },
      });
      this.$emit('add', this.$options?.key);
      this.$tracking({
        event: 'buyInteraction',
        buyInteraction: `${this.trackingBasePlanName} add-ons`,
        buySelection: `${this.trackingPlanName} - ${this.trackingAddAction}`,
        uiLocation: AddOnGroupTrackingUILocation[this.group],
        flowType: getTrackingFlowType()
      })
      this.$nextTick(this.focusToConfig);
    },
    mx_desc() {
      return <div class="opc-addon__description first-capitalize">
        {this.description}
      </div>;
    }
  },
  watch:{
    currentPlan:{
      handler(newVal) {
        Object.keys(newVal).forEach(key => {
          //this.$set(key, newVal[key]);
          this[key] = newVal[key];
        });
      },
      immediate: true,
    }
  }
};
</script>
